import React, { useState, useEffect } from 'react';
import { Truck, Plus, Search, Edit, Trash2, MapPin, Building } from 'lucide-react';
import { API_BASE_URL, maskCnpjCpf } from '../global';
import Modal from '../components/Modal';
import { showAlert, showConfirm } from '../components/AlertPopup';
import './FormPage.css';

export default function Carriers() {
  const [carriers, setCarriers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    nome: '',
    cnpj_cpf: '',
    ie: '',
    logradouro: '',
    numero: '',
    bairro: '',
    cidade: '',
    uf: 'SP'
  });

  useEffect(() => {
    fetchCarriers();
  }, []);

  const fetchCarriers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/transportadoras.php`);
      if (!response.ok) {
        console.error('HTTP Error:', response.status);
        return;
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        setCarriers(data);
      }
    } catch (e) {
      console.error('Erro ao buscar transportadoras:', e);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const val = name === 'cnpj_cpf' ? maskCnpjCpf(value) : value;
    setFormData(prev => ({
      ...prev,
      [name]: val
    }));
  };

  const handleOpenModal = (carrier = null) => {
    if (carrier) {
      setEditingId(carrier.id);
      setFormData({
        nome: carrier.nome || '',
        cnpj_cpf: carrier.cnpj_cpf || '',
        ie: carrier.ie || '',
        logradouro: carrier.logradouro || '',
        numero: carrier.numero || '',
        bairro: carrier.bairro || '',
        cidade: carrier.cidade || '',
        uf: carrier.uf || 'SP'
      });
    } else {
      setEditingId(null);
      setFormData({
        nome: '',
        cnpj_cpf: '',
        ie: '',
        logradouro: '',
        numero: '',
        bairro: '',
        cidade: '',
        uf: 'SP'
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome) {
      showAlert('Aviso', 'Preencha a Razão Social / Nome da transportadora');
      return;
    }

    const method = editingId ? 'PUT' : 'POST';
    const payload = editingId ? { ...formData, id: editingId } : formData;

    try {
      const response = await fetch(`${API_BASE_URL}/transportadoras.php`, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const res = await response.json();
      if (res.success) {
        showAlert('Sucesso', editingId ? 'Transportadora atualizada com sucesso!' : 'Transportadora cadastrada com sucesso!');
        handleCloseModal();
        fetchCarriers();
      } else {
        showAlert('Erro', res.error || 'Erro ao salvar transportadora');
      }
    } catch (err) {
      console.error(err);
      showAlert('Erro', 'Falha de comunicação com o servidor');
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm('Confirmação', 'Deseja realmente remover esta transportadora?');
    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/transportadoras.php?id=${id}`, {
        method: 'DELETE'
      });
      const res = await response.json();
      if (res.success) {
        showAlert('Sucesso', 'Transportadora removida!');
        fetchCarriers();
      } else {
        showAlert('Erro', res.error || 'Erro ao deletar transportadora');
      }
    } catch (err) {
      console.error(err);
      showAlert('Erro', 'Erro de conexão com o servidor');
    }
  };

  const filteredCarriers = carriers.filter((c) =>
    (c.nome || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.cnpj_cpf || '').includes(searchTerm) ||
    (c.cidade || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="page-container animate-fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Truck size={28} color="var(--primary-color)" /> Gestão de Transportadoras
          </h1>
          <p className="page-subtitle">Cadastre e gerencie empresas transportadoras para emissão de frete e NF-e</p>
        </div>
        <button className="btn-primary" style={{ width: 'auto', padding: '0.75rem 1.25rem' }} onClick={() => handleOpenModal()}>
          <Plus size={18} /> Nova Transportadora
        </button>
      </div>

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div className="search-bar" style={{ width: '100%', maxWidth: '380px' }}>
            <Search className="search-icon" size={18} />
            <input
              type="text"
              className="search-input"
              placeholder="Buscar por Nome, CNPJ ou Cidade..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="table-responsive" style={{ marginTop: '1rem' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Razão Social / Nome</th>
                <th>CNPJ / CPF</th>
                <th>Inscrição Estadual</th>
                <th>Endereço / Localidade</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredCarriers.length > 0 ? (
                filteredCarriers.map((carrier) => (
                  <tr key={carrier.id}>
                    <td>#{carrier.id}</td>
                    <td>
                      <div className="font-medium" style={{ color: 'var(--text-main)', fontWeight: '600' }}>
                        {carrier.nome}
                      </div>
                    </td>
                    <td>{carrier.cnpj_cpf || 'Não informado'}</td>
                    <td>{carrier.ie || 'Isento / Não inf.'}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem' }}>
                        <MapPin size={14} color="var(--text-muted)" />
                        {carrier.logradouro ? `${carrier.logradouro}, ${carrier.numero || 'S/N'} - ${carrier.bairro || ''}` : 'Endereço não cadastrado'}
                        {carrier.cidade && ` (${carrier.cidade}/${carrier.uf})`}
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-icon" onClick={() => handleOpenModal(carrier)} title="Editar">
                          <Edit size={16} />
                        </button>
                        <button className="btn-icon danger" onClick={() => handleDelete(carrier.id)} title="Excluir">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--text-muted)' }}>
                    {searchTerm ? 'Nenhuma transportadora encontrada para a busca.' : 'Nenhuma transportadora cadastrada ainda.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODAL CADASTRO / EDIÇÃO */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingId ? 'Editar Transportadora' : 'Cadastrar Nova Transportadora'}
        maxWidth="680px"
      >
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', width: '100%', padding: '0.25rem 0' }}>
          
          {/* SEÇÃO 1: DADOS DA EMPRESA */}
          <div>
            <div className="form-section-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', fontSize: '0.9rem', fontWeight: '600', color: 'var(--text-main)' }}>
              <Building size={16} color="var(--primary-color)" /> Dados da Empresa
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.85rem' }}>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Razão Social / Nome <span className="text-danger">*</span></label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleInputChange}
                  className="standard-input"
                  required
                  placeholder="Ex: Expresso Translog Ltda"
                />
              </div>
              <div className="form-group">
                <label>CNPJ ou CPF</label>
                <input
                  type="text"
                  name="cnpj_cpf"
                  value={formData.cnpj_cpf}
                  onChange={handleInputChange}
                  className="standard-input"
                  placeholder="00.000.000/0000-00"
                />
              </div>
              <div className="form-group">
                <label>Inscrição Estadual (IE)</label>
                <input
                  type="text"
                  name="ie"
                  value={formData.ie}
                  onChange={handleInputChange}
                  className="standard-input"
                  placeholder="IE ou Isento"
                />
              </div>
            </div>
          </div>

          {/* SEÇÃO 2: ENDEREÇO & LOCALIZAÇÃO */}
          <div>
            <div className="form-section-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', fontSize: '0.9rem', fontWeight: '600', color: 'var(--text-main)', borderTop: '1px solid var(--border-color)', paddingTop: '0.85rem' }}>
              <MapPin size={16} color="var(--primary-color)" /> Endereço Completo
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <div style={{ display: 'flex', gap: '0.85rem' }}>
                <div className="form-group" style={{ flex: 3 }}>
                  <label>Logradouro (Rua, Av.)</label>
                  <input
                    type="text"
                    name="logradouro"
                    value={formData.logradouro}
                    onChange={handleInputChange}
                    className="standard-input"
                    placeholder="Rua / Avenida"
                  />
                </div>
                <div className="form-group" style={{ flex: 1, minWidth: '90px' }}>
                  <label>Número</label>
                  <input
                    type="text"
                    name="numero"
                    value={formData.numero}
                    onChange={handleInputChange}
                    className="standard-input"
                    placeholder="100"
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.85rem' }}>
                <div className="form-group" style={{ flex: 2 }}>
                  <label>Bairro</label>
                  <input
                    type="text"
                    name="bairro"
                    value={formData.bairro}
                    onChange={handleInputChange}
                    className="standard-input"
                    placeholder="Bairro"
                  />
                </div>
                <div className="form-group" style={{ flex: 2 }}>
                  <label>Cidade</label>
                  <input
                    type="text"
                    name="cidade"
                    value={formData.cidade}
                    onChange={handleInputChange}
                    className="standard-input"
                    placeholder="Cidade"
                  />
                </div>
                <div className="form-group" style={{ flex: 1, minWidth: '70px', maxWidth: '90px' }}>
                  <label>UF</label>
                  <input
                    type="text"
                    name="uf"
                    value={formData.uf}
                    onChange={handleInputChange}
                    className="standard-input"
                    maxLength="2"
                    style={{ textTransform: 'uppercase' }}
                    placeholder="SP"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="form-actions" style={{ justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem', paddingTop: '0.85rem', borderTop: '1px solid var(--border-color)' }}>
            <button type="button" className="btn-outline" onClick={handleCloseModal}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '0.65rem 1.5rem' }}>
              {editingId ? 'Salvar Alterações' : 'Cadastrar Transportadora'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
